import { useEffect, useState } from 'react';
import { FiSmartphone, FiMail, FiMapPin } from 'react-icons/fi';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

export default function Contact() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Estado para el selector de idioma
  const [selectedLanguage, setSelectedLanguage] = useState('spanish');

  const contactInfo = [
    {
      icon: <FiSmartphone className="text-sky-900 w-8 h-8" />,
      text: '+34 971 896 003',
      label: 'Teléfono Fijo',
      link: 'tel:+34971896003',
    },
    {
      icon: <FiSmartphone className="text-sky-900 w-8 h-8" />,
      text: '+34 666 957 414',
      label: 'Teléfono Móvil',
      link: 'tel:+34654321987',
    },
    {
      icon: <FiMail className="text-sky-900 w-8 h-8" />,
      text: 'eventos@txokomenorca.com',
      label: 'Correo Electrónico',
      link: 'mailto:eventos@txokomenorca.com',
    },
    {
      icon: <FiMapPin className="text-sky-900 w-8 h-8" />,
      text: (
        <>
          Carrer Gregal, 11
          <br />
          Binisafua, Menorca
          <br />
          07711
        </>
      ),
      label: 'Dirección',
    },
  ];

  const faqData = [
    {
      question: '¿Cuál es el horario del restaurante?',
      answer: 'Nuestro horario es de martes a domingo, de 12:00 a 17:00 y de 19:00 a 23:00. Lunes de 19:00 a 23:00.',
    },
    {
      question: '¿Aceptan reservas?',
      answer: 'Sí, aceptamos reservas. Puedes reservar a través de nuestro número de teléfono o a traves de cover manager.',
    },
    {
      question: '¿Hay opciones vegetarianas o veganas?',
      answer: 'Sí, tenemos opciones vegetarianas y veganas disponibles. Por favor, consulta nuestra carta o pregunta a nuestro personal.',
    },
    {
      question: '¿Admiten mascotas?',
      answer:
        'Sí, somos un establecimiento pet-friendly. Las mascotas son bienvenidas en nuestra terraza.',
    },
    {
      question: '¿Hay aparcamiento disponible cerca del restaurante?',
      answer:
        'Sí, el restaurante dispone de aparcamiento propio.',
    },
    {
      question: '¿Ofrecen servicio de comida para llevar?',
      answer:
        'Sí, ofrecemos servicio de comida para llevar. Puedes hacer tu pedido por teléfono y recogerlo en el restaurante.',
    },
    // Añade más preguntas aquí si lo necesitas
  ];

  const [openQuestion, setOpenQuestion] = useState(null);

  const toggleFAQ = (index) => {
    setOpenQuestion((prevIndex) => (prevIndex === index ? null : index));
  };

  // URLs del sistema de reservas para cada idioma
  const reservationUrls = {
    spanish: 'https://www.covermanager.com/reservation/module_restaurant/txoko-menorca/spanish',
    english: 'https://www.covermanager.com/reservation/module_restaurant/txoko-menorca/english',
    french: 'https://www.covermanager.com/reservation/module_restaurant/txoko-menorca/french'
  };

  // Textos para el selector de idiomas
  const languageLabels = {
    spanish: 'Español',
    english: 'English',
    french: 'Français'
  };

  // Función para cambiar el idioma seleccionado
  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
  };

  // Efecto para cargar el script de iframeResizer
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.covermanager.com/js/iframeResizer.min.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div className="font-montserrat bg-gray-50">
      <Helmet>
        <title>Contacto - Txoko</title>
        <meta
          name="description"
          content="Contacta con Txoko en Menorca. Estamos disponibles para resolver tus dudas, reservas y sugerencias. ¡Ven a disfrutar de la mejor comida de mar!"
        />
      </Helmet>

      <div className="container mx-auto px-6 py-10 sm:py-20 lg:py-32">
        {/* Información de Contacto (Márgenes laterales) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-10 px-8 md:px-16 lg:px-24">
          {contactInfo.map((item, index) => (
            <motion.div
              key={index}
              className="flex flex-col items-center justify-center"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
            >
              <div className="mb-4">{item.icon}</div>
              <h3 className="font-barlow text-xl text-gray-900 uppercase mb-2">
                {item.label}
              </h3>
              <p className="text-lg font-ubuntu text-center">
                {item.link ? (
                  <a href={item.link} className="hover:underline">
                    {item.text}
                  </a>
                ) : (
                  item.text
                )}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Sistema de Reservas con Selector de Idioma */}
        <div className="mt-16 px-4 sm:px-8 md:px-16 lg:px-24">
          <motion.div
            className="bg-white rounded-lg shadow-lg p-4 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl sm:text-3xl text-gray-900 font-barlow font-bold uppercase mb-4 sm:mb-8 text-center">
              Haz tu Reserva
            </h2>
            
            {/* Selector de idiomas - ajustado para mejor visualización en móviles */}
            <div className="flex justify-center mb-6">
              <div className="flex flex-col sm:flex-row rounded-md shadow-sm" role="group">
                {Object.keys(languageLabels).map((language, idx) => (
                  <button
                    key={language}
                    type="button"
                    onClick={() => handleLanguageChange(language)}
                    className={`px-4 py-2 text-sm font-medium border ${
                      selectedLanguage === language
                        ? 'bg-sky-900 text-white border-sky-900'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                    } ${
                      idx === 0
                        ? 'rounded-t-lg sm:rounded-t-none sm:rounded-l-lg' 
                        : idx === Object.keys(languageLabels).length - 1
                          ? 'rounded-b-lg sm:rounded-b-none sm:rounded-r-lg'
                          : ''
                    } ${
                      idx !== 0 ? 'border-t-0 sm:border-t sm:border-l-0' : ''
                    }`}
                  >
                    {languageLabels[language]}
                  </button>
                ))}
              </div>
            </div>
            
            {/* CoverManager iframe */}
            <div className="w-full overflow-x-hidden">
              <iframe
                id="txoko-menorca"
                title="Reservas"
                src={reservationUrls[selectedLanguage]}
                allow="payment"
                frameBorder="0"
                height="650"
                width="100%"
                className="w-full"
                style={{ 
                  transformOrigin: "0 0", 
                  transform: "scale(0.85)",
                  width: "118%", /* Compensar la escala 1/0.85 ≈ 1.18 */
                  maxWidth: "118%" 
                }}
                onLoad={() => {
                  if (window.iFrameResize) {
                    window.iFrameResize({
                      heightCalculationMethod: 'lowestElement'
                    });
                  }
                }}
              ></iframe>
            </div>
          </motion.div>
        </div>

        {/* Mapa Interactivo (Márgenes laterales) */}
        <div className="mt-16 px-8 md:px-16 lg:px-24">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d851.5796542697022!2d4.215775623609821!3d39.82849589159506!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1295893e0339f1b9%3A0x5ddac3d01e3a1569!2sTxoko%20Menorca!5e0!3m2!1sen!2ses!4v1743105546530!5m2!1sen!2ses"
            title="Nuestra ubicación en Google Maps"
            style={{ border: 0, width: "100%", height: "450px" }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade">
          </iframe>
        </div>

        {/* FAQ (Márgenes laterales) */}
        <div className="mt-16 px-8 md:px-16 lg:px-24">
          <h2 className="text-3xl text-gray-900 font-barlow font-bold uppercase mb-8 text-center">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-300 rounded-lg"
              >
                <button
                  className="w-full p-4 text-left flex items-center justify-between"
                  onClick={() => toggleFAQ(index)}
                >
                  <h4 className="text-lg font-medium text-gray-900 font-barlow">
                    {faq.question}
                  </h4>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <svg
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </button>
                {openQuestion === index && (
                  <div className="p-4 pt-0">
                    <p className="text-gray-700 font-montserrat">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}